import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Dialog, Slide, IconButton, Grid } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ThemedDialogContent from "./ThemedDialogContent";
import ThemedDialogTitle from "./ThemedDialogTitle";
import ThemedDialogActions from "./ThemedDialogActions";
import ThemedTextField from "../ThemedTextField";
import ThemedAutocomplete from "../ThemedAutocomplete";
import PhoneNumberFormat from "../PhoneNumberFormat";
import { decoratedFetch } from "../../req_utils";
import {
  DARK_THEME_BODY_COLOR,
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
} from "../../constants";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileEditContactDialog({
  parentState,
  createDialogOpen,
  handleClose,
  handleCreateClose,
  onContactUpdate,
  onCreateContact,
  accountId,
  setSnackbarMessage,
}) {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [contact, setContact] = useState(null);
  const [contactType, setContactType] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cellNo, setCellNo] = useState("");
  const [email, setEmail] = useState("");
  const [contactTypes, setContactTypes] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  // Check if all required fields are filled
  const isFormValid =
    contactType.trim() !== "" &&
    firstName.trim() !== "" &&
    lastName.trim() !== "";

  const initializeContactData = (selectedContact) => {
    if (selectedContact) {
      setContact(selectedContact);
      setContactType(selectedContact.contact_type || "");
      setFirstName(selectedContact.first_name || "");
      setLastName(selectedContact.last_name || "");
      setCellNo(selectedContact.cell_no || "");
      setEmail(selectedContact.email || "");
    }
  };

  const resetCreateForm = () => {
    setContactType("");
    setFirstName("");
    setLastName("");
    setCellNo("");
    setEmail("");
    setCreateLoading(false);
  };

  const listContactTypes = () => {
    decoratedFetch("/list_contact_types")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setContactTypes(data);
        } else {
          console.error("Expected an array of contact types, received:", data);
          setContactTypes([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching contact types:", error);
        setContactTypes([]);
      });
  };

  useEffect(() => {
    if (parentState !== null && parentState?.selectedContact !== null) {
      initializeContactData(parentState?.selectedContact);
    } else {
      setContact(null);
    }
    if (parentState !== null || createDialogOpen) listContactTypes();
  }, [parentState, createDialogOpen]);

  useEffect(() => {
    if (createDialogOpen) {
      resetCreateForm();
    }
  }, [createDialogOpen]);

  const updateContact = useCallback(
    (fieldName, value) => {
      if (!contact) return;

      decoratedFetch(`/update_contact/${contact.id}`, {
        method: "PUT",
        body: JSON.stringify({ [fieldName]: value }),
      })
        .then((response) => {
          if (response.status === 200) {
            setSnackbarMessage("Contact saved");
            if (onContactUpdate) {
              const updatedContact = { ...contact };
              updatedContact[fieldName] = value;
              // Update local contact state to reflect the change
              setContact(updatedContact);
              onContactUpdate(updatedContact);
            }
            return;
          }
          throw new Error("Error message generated");
        })
        .catch((error) => {
          console.error("Error saving contact", error);
          setSnackbarMessage("Failed to save contact");
        });
    },
    [contact, onContactUpdate, setSnackbarMessage],
  );

  const handleContactTypeChange = (event, newValue) => {
    const newContactType = newValue;
    setContactType(newContactType);
  };

  const handleContactTypeBlur = () => {
    if (parentState !== null && contactType !== contact?.contact_type) {
      updateContact("contact_type", contactType);
    }
  };

  const handleFirstNameChange = (event) => {
    const newFirstName = event.target.value;
    setFirstName(newFirstName);
  };

  const handleFirstNameBlur = () => {
    if (parentState !== null && firstName !== contact?.first_name) {
      updateContact("first_name", firstName);
    }
  };

  const handleLastNameChange = (event) => {
    const newLastName = event.target.value;
    setLastName(newLastName);
  };

  const handleLastNameBlur = () => {
    if (parentState !== null && lastName !== contact?.last_name) {
      updateContact("last_name", lastName);
    }
  };

  const handleCellNoChange = (event) => {
    const newCellNo = event.target.value;
    setCellNo(newCellNo);
  };

  const handleCellNoBlur = () => {
    if (parentState !== null && cellNo !== contact?.cell_no) {
      updateContact("cell_no", cellNo);
    }
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
  };

  const handleEmailBlur = () => {
    if (parentState !== null && email !== contact?.email) {
      updateContact("email", email);
    }
  };

  const handleCreateContact = () => {
    setCreateLoading(true);
    const payload = {
      contact_type: contactType,
      first_name: firstName,
      last_name: lastName,
      cell_no: cellNo,
      email: email,
      account_id: accountId,
    };

    decoratedFetch("/create_contact", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        throw new Error("Failed to create contact");
      })
      .then((data) => {
        setCreateLoading(false);
        setSnackbarMessage("Contact created");
        if (onCreateContact) {
          onCreateContact(data);
        }
      })
      .catch((error) => {
        console.error("Error creating contact:", error);
        setCreateLoading(false);
        setSnackbarMessage("Failed to create contact");
      });
  };

  return (
    <>
      <Dialog
        fullScreen
        open={parentState !== null || createDialogOpen}
        onClose={parentState !== null ? handleClose : handleCreateClose}
        TransitionComponent={Transition}
        aria-labelledby="edit-contact-dialog-title"
      >
        <ThemedDialogTitle
          id="edit-contact-dialog-title"
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: "8px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: darkThemeEnabled
                  ? DARK_THEME_TEXT_COLOR
                  : LIGHT_THEME_TEXT_COLOR,
                fontSize: "1rem",
                fontWeight: "bold",
                marginRight: "18px",
              }}
            >
              {parentState !== null
                ? `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() ||
                  "Edit Contact"
                : "Create Contact"}
            </div>
            <div>
              <IconButton
                edge="start"
                color="inherit"
                onClick={parentState !== null ? handleClose : handleCreateClose}
                aria-label="close"
                style={{
                  color: darkThemeEnabled
                    ? DARK_THEME_TEXT_COLOR
                    : LIGHT_THEME_TEXT_COLOR,
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </ThemedDialogTitle>
        <ThemedDialogContent
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container sx={{ py: "20px", px: "15px" }}>
            <Grid item xs={12}>
              <ThemedAutocomplete
                id="contact-type"
                value={contactType}
                freeSolo
                options={contactTypes.map((option) => option.display_name)}
                renderInput={(params) => (
                  <ThemedTextField {...params} label="Type" size="small" />
                )}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    handleContactTypeChange(event, newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    handleContactTypeChange(event, newValue);
                  }
                }}
                onBlur={handleContactTypeBlur}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="First Name"
                fullWidth
                variant="outlined"
                value={firstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstNameBlur}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Last Name"
                fullWidth
                variant="outlined"
                value={lastName}
                onChange={handleLastNameChange}
                onBlur={handleLastNameBlur}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Cell No."
                fullWidth
                variant="outlined"
                value={cellNo}
                onChange={handleCellNoChange}
                onBlur={handleCellNoBlur}
                size="small"
                InputProps={{
                  inputComponent: PhoneNumberFormat,
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                size="small"
              />
            </Grid>
          </Grid>
        </ThemedDialogContent>
        {parentState === null && (
          <ThemedDialogActions>
            <LoadingButton
              loading={createLoading}
              disabled={!isFormValid}
              sx={{
                backgroundColor: createLoading ? "#565656 !important" : "auto",
                "&.Mui-disabled": {
                  backgroundColor: darkThemeEnabled
                    ? "#666666 !important"
                    : "#e0e0e0 !important",
                  color: darkThemeEnabled
                    ? "#999999 !important"
                    : "#757575 !important",
                },
              }}
              variant="contained"
              onClick={handleCreateContact}
            >
              Create
            </LoadingButton>
          </ThemedDialogActions>
        )}
      </Dialog>
    </>
  );
}
